import React, { useEffect, useState } from "react";

import { IconButton } from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { useMutation } from "@tanstack/react-query";
import { colors } from "@zapalacorp/tokens";
import { AxiosError } from "axios";
import Button, { Color } from "Components/Form/Button";
import Checkbox from "Components/Form/Checkbox";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { useTranslation } from "react-i18next";
import "./style/style.scss";
import { ReactSVG } from "react-svg";
import CommunicationShipmentMovingService from "Services/CommunicationShipmentMoving.service";
import { checkError } from "Util/checkError";
import { isNullOrEmpty } from "Util/common";
import { ISubscriber } from "Models/shipments";
const SendMessageIcon = require("Assets/icons/send-2.svg");

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: "12px",
  padding: "24px 0px",
};

const CLOSE_ICON = require("Assets/icons/close.svg");
const EMAIL_ICON = require("Assets/icons/email-outline.svg");
const SMS_ICON = require("Assets/icons/outline-message-text.svg");

interface IModalSendCommunicationConfig {
  id: number;
  dataList?: any;
  isOpen?: boolean;
  label?: string;
  onClose?: () => void | Function;
  onSubmit?: () => void | Function;
}

const NOTIFY_VIA = {
  EMAIL: "EMAIL",
  SMS: "SMS",
};

const ModalSendCommunication = (props: IModalSendCommunicationConfig) => {
  const { t: translate } = useTranslation();
  const [selectedType, setSelectedType] = useState<any>({ email: false, sms: false });
  const [dataSelectedList, setDataSelectedList] = useState<any[]>([]);
  const [isShowOptionSms, setIsShowOptionSms] = useState<boolean>(false);
  const [isShowOptionEmail, setIsShowOptionEmail] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isExistMobileNumber = (dataList: any[]) => {
    if (!dataList) return false;

    return dataList.every((item) => item?.notifyViaSms);
  };

  const isExistEmail = (dataList: any[]) => {
    if (!dataList) return false;

    return dataList.every((item) => item?.notifyViaEmail);
  };

  const { mutate: shareSMS, isSuccess: isSuccessSMS } = useMutation(
    ["subscribers.confirm.sms"],
    (share: { shipmentId: number; subscribers: ISubscriber[] }) => {
      setIsLoading(true);
      return CommunicationShipmentMovingService.sendSms(share);
    },
    {
      onSuccess: () => {
        props?.onSubmit?.();
        setIsLoading(false);
      },
      onError: (error: AxiosError<Error>) => {
        setIsLoading(false);
        checkError(error, translate("anErrorOccurredTryAgain"));
      },
    }
  );

  const { mutate: shareEmail, isSuccess: isSuccessEmail } = useMutation(
    ["subscriber.confirm.email"],
    (share: { shipmentId: number; subscribers: ISubscriber[] }) => {
      setIsLoading(true);
      return CommunicationShipmentMovingService.sendEmail(share);
    },
    {
      onSuccess: () => {
        props?.onSubmit?.();
        setIsLoading(false);
      },
      onError: (error: AxiosError<Error>) => {
        setIsLoading(false);
        checkError(error, translate("anErrorOccurredTryAgain"));
      },
    }
  );

  const handleSend = () => {
    if (selectedType?.email) {
      handleSendEmail();
    }
    if (selectedType?.sms) {
      handleSendSMS();
    }
  };

  useEffect(() => {
    if (isSuccessEmail || isSuccessSMS) {
      NotificationToast.success(translate("notificationSentSuccessfully"));
      props?.onClose?.();
    }
  }, [isSuccessEmail, isSuccessSMS]);

  const handleSendEmail = () => {
    const beSharedSubscribers = dataSelectedList
      ?.map((item: ISubscriber) => ({
        firstName: item?.firstName || "",
        lastName: item?.lastName || "",
        email: item?.email || "",
      }))
      ?.filter((i) => !!i?.email);
    if (props?.id) shareEmail({ shipmentId: props?.id, subscribers: beSharedSubscribers });
  };

  const handleSendSMS = () => {
    const beSharedSubscribers = dataSelectedList
      ?.map((item: ISubscriber) => ({
        firstName: item?.firstName || "",
        lastName: item?.lastName || "",
        mobile: item?.mobile,
      }))
      ?.filter((i) => !!i?.mobile);
    if (props?.id) shareSMS({ shipmentId: props?.id, subscribers: beSharedSubscribers });
  };

  useEffect(() => {
    setDataSelectedList(props?.dataList);
    setIsShowOptionSms(isExistMobileNumber(props?.dataList));
    setIsShowOptionEmail(isExistEmail(props?.dataList));
  }, [props?.dataList]);

  return (
    <Modal
      open={!!props?.isOpen}
      onClose={props?.onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
            paddingLeft: "32px",
            paddingRight: "32px",
          }}
        >
          <div
            style={{
              fontSize: "22px",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "28px",
              fontFamily: "Manrope",
              color: colors.gray600,
            }}
          >
            {props?.label}
          </div>
          <IconButton onClick={props?.onClose}>
            <img src={CLOSE_ICON} />
          </IconButton>
        </div>
        {/* Body */}
        <div
          style={{
            display: "flex",
            padding: "32px",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "12px",
            alignSelf: "stretch",
          }}
        >
          <div
            style={{
              cursor: isShowOptionEmail ? "pointer" : "not-allowed",
              background: isShowOptionEmail ? colors.white : colors.gray200,

              display: "flex",
              padding: "12px 16px",
              justifyContent: "space-between",
              alignItems: "center",
              alignSelf: "stretch",
              borderRadius: "8px",
              border: `1px solid ${selectedType?.email ? colors.brandBlue500 : colors.gray200}`,
            }}
            onClick={() => {
              if (isShowOptionEmail) {
                setSelectedType({ ...selectedType, email: !selectedType?.email });
              }
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "16px",
                alignItems: "center",
                marginTop: "2px",
              }}
            >
              <div
                style={{
                  color: selectedType?.email ? colors.brandBlue500 : colors.gray700,
                }}
              >
                <ReactSVG src={EMAIL_ICON} />
              </div>
              <div
                style={{
                  color: selectedType?.email ? colors.brandBlue500 : colors.gray700,
                  fontFamily: "Manrope",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 700,
                  lineHeight: "20px",
                  letterSpacing: "0.1px",
                }}
              >
                {translate("email")}
              </div>
            </div>
            <Checkbox checked={selectedType?.email} />
          </div>
          <div
            style={{
              cursor: isShowOptionSms ? "pointer" : "not-allowed",
              background: isShowOptionSms ? colors.white : colors.gray200,
              display: "flex",
              padding: "12px 16px",
              justifyContent: "space-between",
              alignItems: "center",
              alignSelf: "stretch",
              borderRadius: "8px",
              border: `1px solid ${selectedType?.sms ? colors.brandBlue500 : colors.gray200}`,
            }}
            onClick={() => {
              if (isShowOptionSms) {
                setSelectedType({ ...selectedType, sms: !selectedType?.sms });
              }
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "16px",
                alignItems: "center",
                marginTop: "2px",
              }}
            >
              <div
                style={{
                  color: selectedType?.sms ? colors.brandBlue500 : colors.gray700,
                }}
              >
                <ReactSVG src={SMS_ICON} />
              </div>
              <div
                style={{
                  color: selectedType?.sms ? colors.brandBlue500 : colors.gray700,
                  fontFamily: "Manrope",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 700,
                  lineHeight: "20px",
                  letterSpacing: "0.1px",
                }}
              >
                {translate("sms")}
              </div>
            </div>
            <Checkbox checked={selectedType?.sms} disabled={!isShowOptionSms} />
          </div>
        </div>
        {/* Footer */}
        <div
          style={{
            borderTop: "1px solid #e4e7ec",
          }}
        >
          <div
            style={{
              display: "flex",
              paddingTop: "24px",
              justifyContent: "flex-end",
              alignItems: "center",
              alignSelf: "stretch",
              width: "100%",
              paddingRight: "28px",
            }}
          >
            <Button customColor={Color.TEXT_INFO} onClick={props?.onClose}>
              {translate("cancel")}
            </Button>
            <Button
              style={{
                padding: " 10px 20px 10px 16px",
              }}
              startIcon={
                <div
                  style={{
                    color: "white",
                  }}
                >
                  <ReactSVG src={SendMessageIcon} />
                </div>
              }
              loading={isLoading}
              customColor={Color.SUCCESS}
              onClick={handleSend}
              disabled={isNullOrEmpty(selectedType)}
            >
              {translate("send")}
            </Button>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default ModalSendCommunication;
